import { svgEl } from "./dom";

export interface PlotSeries {
  type?: "line" | "scatter" | "area" | "bar";
  points: Array<[number, number]>;
  color?: string;
  label?: string;
  strokeWidth?: number;
  dashed?: boolean;
  radius?: number;
  opacity?: number;
}

export interface PlotOptions {
  width?: number;
  height?: number;
  series: PlotSeries[];
  xLabel?: string;
  yLabel?: string;
  xScale?: "linear" | "log";
  yScale?: "linear" | "log";
  xDomain?: [number, number];
  yDomain?: [number, number];
  xFormat?: (n: number) => string;
  yFormat?: (n: number) => string;
  legend?: boolean;
  title?: string;
}

const SVG_NS = "http://www.w3.org/2000/svg";
export const PALETTE = ["#5eead4", "#a78bfa", "#fca5a5", "#fcd34d", "#93c5fd", "#f9a8d4"];

/** Build a self-contained, responsive SVG plot. Pure DOM, no dependencies. */
export function plot(options: PlotOptions): SVGSVGElement {
  const width = options.width ?? 640;
  const height = options.height ?? 320;
  const xScale = options.xScale ?? "linear";
  const yScale = options.yScale ?? "linear";
  const m = { top: options.title ? 30 : 14, right: 16, bottom: 44, left: 60 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;

  const allX: number[] = [];
  const allY: number[] = [];
  for (const s of options.series) {
    for (const [x, y] of s.points) {
      if (xScale === "log" && x <= 0) continue;
      if (yScale === "log" && y <= 0) continue;
      allX.push(x);
      allY.push(y);
    }
  }

  const xd = resolveDomain(options.xDomain, allX, xScale);
  const yd = resolveDomain(options.yDomain, allY, yScale);
  const xTicks = makeTicks(xd, xScale);
  const yTicks = makeTicks(yd, yScale);

  const sx = (v: number) => m.left + project(v, xd, xScale) * plotW;
  const sy = (v: number) => m.top + (1 - project(v, yd, yScale)) * plotH;

  const svg = svgEl("svg", {
    viewBox: `0 0 ${width} ${height}`,
    class: "plot",
    preserveAspectRatio: "xMidYMid meet",
    role: "img",
  }) as SVGSVGElement;
  svg.setAttribute("width", "100%");

  if (options.title) {
    svg.appendChild(
      text(width / 2, 18, options.title, { anchor: "middle", class: "plot-title" }),
    );
  }

  // Gridlines + tick labels.
  const xf = options.xFormat ?? defaultFormat;
  const yf = options.yFormat ?? defaultFormat;
  for (const t of yTicks) {
    const y = sy(t);
    svg.appendChild(line(m.left, y, m.left + plotW, y, "plot-grid"));
    svg.appendChild(text(m.left - 8, y + 4, yf(t), { anchor: "end", class: "plot-tick" }));
  }
  for (const t of xTicks) {
    const x = sx(t);
    svg.appendChild(line(x, m.top, x, m.top + plotH, "plot-grid"));
    svg.appendChild(
      text(x, m.top + plotH + 18, xf(t), { anchor: "middle", class: "plot-tick" }),
    );
  }

  // Axis frame.
  svg.appendChild(line(m.left, m.top + plotH, m.left + plotW, m.top + plotH, "plot-axis"));
  svg.appendChild(line(m.left, m.top, m.left, m.top + plotH, "plot-axis"));

  // Series.
  options.series.forEach((s, i) => {
    const color = s.color ?? PALETTE[i % PALETTE.length];
    const pts = s.points.filter(
      ([x, y]) => !(xScale === "log" && x <= 0) && !(yScale === "log" && y <= 0),
    );
    if (s.type === "scatter") {
      for (const [x, y] of pts) {
        const c = svgEl("circle", {
          cx: sx(x),
          cy: sy(y),
          r: s.radius ?? 2.5,
          fill: color,
          "fill-opacity": s.opacity ?? 0.7,
        });
        svg.appendChild(c);
      }
    } else if (s.type === "bar") {
      const base = m.top + plotH;
      const barW = Math.max(1, (plotW / Math.max(1, pts.length)) * 0.82);
      for (const [x, y] of pts) {
        const top = sy(y);
        svg.appendChild(
          svgEl("rect", {
            x: sx(x) - barW / 2,
            y: Math.min(top, base),
            width: barW,
            height: Math.abs(base - top),
            fill: color,
            "fill-opacity": s.opacity ?? 0.75,
          }),
        );
      }
    } else {
      const d = pts.map(([x, y], j) => `${j === 0 ? "M" : "L"}${sx(x)},${sy(y)}`).join(" ");
      if (s.type === "area") {
        const base = m.top + plotH;
        const area = `${d} L${sx(pts[pts.length - 1][0])},${base} L${sx(pts[0][0])},${base} Z`;
        svg.appendChild(svgEl("path", { d: area, fill: color, "fill-opacity": 0.12, stroke: "none" }));
      }
      const path = svgEl("path", {
        d,
        fill: "none",
        stroke: color,
        "stroke-width": s.strokeWidth ?? 2,
        "stroke-linejoin": "round",
        "stroke-linecap": "round",
      });
      if (s.dashed) path.setAttribute("stroke-dasharray", "5 4");
      svg.appendChild(path);
    }
  });

  // Axis labels.
  if (options.xLabel) {
    svg.appendChild(
      text(m.left + plotW / 2, height - 6, options.xLabel, { anchor: "middle", class: "plot-label" }),
    );
  }
  if (options.yLabel) {
    const t = text(0, 0, options.yLabel, { anchor: "middle", class: "plot-label" });
    t.setAttribute("transform", `translate(16,${m.top + plotH / 2}) rotate(-90)`);
    svg.appendChild(t);
  }

  // Legend (top-right, inside the plot area).
  if (options.legend) {
    const labelled = options.series.filter((s) => s.label);
    labelled.forEach((s, i) => {
      const color = s.color ?? PALETTE[options.series.indexOf(s) % PALETTE.length];
      const gy = m.top + 6 + i * 18;
      const gx = m.left + plotW - 130;
      svg.appendChild(svgEl("rect", { x: gx, y: gy, width: 12, height: 12, rx: 2, fill: color }));
      svg.appendChild(text(gx + 18, gy + 10, s.label!, { anchor: "start", class: "plot-legend" }));
    });
  }

  return svg;
}

function resolveDomain(
  domain: [number, number] | undefined,
  values: number[],
  scale: "linear" | "log",
): [number, number] {
  if (domain) return domain;
  if (values.length === 0) return scale === "log" ? [1, 10] : [0, 1];
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    if (scale === "log") return [min / 2, max * 2];
    const pad = Math.abs(min) || 1;
    return [min - pad, max + pad];
  }
  if (scale === "log") {
    return [Math.pow(10, Math.floor(Math.log10(min))), Math.pow(10, Math.ceil(Math.log10(max)))];
  }
  const { niceMin, niceMax } = niceRange(min, max, 5);
  return [niceMin, niceMax];
}

function project(v: number, [min, max]: [number, number], scale: "linear" | "log"): number {
  if (scale === "log") {
    const lo = Math.log10(min);
    const hi = Math.log10(max);
    return (Math.log10(v) - lo) / (hi - lo);
  }
  return (v - min) / (max - min);
}

function makeTicks([min, max]: [number, number], scale: "linear" | "log"): number[] {
  if (scale === "log") {
    const ticks: number[] = [];
    for (let e = Math.round(Math.log10(min)); Math.pow(10, e) <= max * 1.0001; e++) {
      ticks.push(Math.pow(10, e));
    }
    return ticks;
  }
  const step = niceStep(min, max, 5);
  const ticks: number[] = [];
  for (let v = min; v <= max + step * 0.5; v += step) ticks.push(round(v));
  return ticks;
}

function niceRange(min: number, max: number, count: number): { niceMin: number; niceMax: number } {
  const step = niceStep(min, max, count);
  return { niceMin: Math.floor(min / step) * step, niceMax: Math.ceil(max / step) * step };
}

function niceStep(min: number, max: number, count: number): number {
  const range = niceNum(max - min, false);
  return niceNum(range / (count - 1), true);
}

function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range || 1));
  const frac = (range || 1) / Math.pow(10, exp);
  let nice: number;
  if (round) nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  else nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

function round(v: number): number {
  return Number(v.toPrecision(12));
}

function defaultFormat(n: number): string {
  if (n !== 0 && (Math.abs(n) >= 1e5 || Math.abs(n) < 1e-3)) return n.toExponential(0);
  return String(round(n));
}

function line(x1: number, y1: number, x2: number, y2: number, cls: string): SVGElement {
  return svgEl("line", { x1, y1, x2, y2, class: cls });
}

function text(
  x: number,
  y: number,
  content: string,
  opts: { anchor: string; class: string },
): SVGElement {
  const t = document.createElementNS(SVG_NS, "text");
  t.setAttribute("x", String(x));
  t.setAttribute("y", String(y));
  t.setAttribute("text-anchor", opts.anchor);
  t.setAttribute("class", opts.class);
  t.textContent = content;
  return t;
}
